import React, { useEffect, useState } from 'react'
import CustomRow from '../../../reusable-components/CustomRow'
import { Button, Card, Col, Space, Steps, message, theme } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheckCircle, faChevronLeft, faChevronRight, faXmarkCircle } from '@fortawesome/free-solid-svg-icons'
import { handleError } from '../../../lib/utils'
import { useDispatch } from 'react-redux'
import coreReducer from '../../../../core/utils/reducer'
import { createCarePlanTemplate, updateCarePlanTemplate } from '../../Chronic Care Plans/utils/utils'
import GeneralForm from '../../Common/WizardSteps/GeneralForm'
import DataTrackerForm from '../../Common/WizardSteps/DataTrackerForm'
import VisitTrackers from '../../Common/WizardSteps/VisitTrackers'
import Summary from '../../Common/WizardSteps/Summary'
import MedicationManagement from './MedicationManagement'
import DiagnosticsManagement from './DiagnosticsManagement'

const TreatmentPlanTemplateForm = ({ isNew, recordData, dataPoints, refreshView, isMonitoring }) => {
  const [currentStep, setCurrentStep] = useState(0)
  const [general, setGeneral] = useState({
    name: recordData ? recordData.name : '',
    description: recordData ? recordData.description : ''
  })
  const [data, setData] = useState(
    recordData && recordData.tracking ? recordData.tracking.filter((i) => i.trackerType === 'data') : []
  )
  const [visits, setVisits] = useState(
    recordData && recordData.tracking ? recordData.tracking.filter((i) => i.trackerType === 'visits') : []
  )
  const [medications, setMedications] = useState(recordData && recordData.medications ? recordData.medications : [])
  const [diagnosticTests, setDiagnosticTests] = useState(
    recordData && recordData.diagnosticTests ? recordData.diagnosticTests : []
  )

  const dispatch = useDispatch()
  const { token } = theme.useToken()
  const planType = 'treatment'
  const planLabel = 'Treatment Plan'

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const handleAddTracking = (key, selection) => {
    let tmpData = []
    let tmpVisits = []
    const parsedSelection = JSON.parse(selection)

    switch (key) {
      case 'data':
        tmpData = JSON.parse(JSON.stringify(data))
        tmpData.push({
          dataLabel: parsedSelection.label,
          dataKey: parsedSelection.key,
          count: 1,
          trackerType: 'data',
          intervalDays: 180
        })
        setData(tmpData)
        break
      case 'visits':
        tmpVisits = JSON.parse(JSON.stringify(visits))
        tmpVisits.push({
          dataLabel: parsedSelection.label,
          dataKey: parsedSelection.key,
          count: 1,
          trackerType: 'visits',
          intervalDays: 180
        })
        setVisits(tmpVisits)
        break
      default:
        break
    }
  }

  const handleUpdateTracker = (value, index, property, key) => {
    let tmpData = []
    let tmpVisits = []

    switch (key) {
      case 'data':
        tmpData = JSON.parse(JSON.stringify(data))
        tmpData[index][property] = value
        setData(tmpData)
        break
      case 'visits':
        tmpVisits = JSON.parse(JSON.stringify(visits))
        tmpVisits[index][property] = value
        setVisits(tmpVisits)
        break
      default:
        break
    }
  }

  const handleRemoveTracker = (index, key) => {
    let tmpData = []
    let tmpVisits = []

    switch (key) {
      case 'data':
        tmpData = JSON.parse(JSON.stringify(data))
        tmpData.splice(index, 1)
        setData(tmpData)
        break
      case 'visits':
        tmpVisits = JSON.parse(JSON.stringify(visits))
        tmpVisits.splice(index, 1)
        setVisits(tmpVisits)
        break
      default:
        break
    }
  }

  const checkGeneralStatus = () => {
    return !!general.name
  }

  const handleFinalizePlan = async () => {
    // Create payload object with all treatment plan data
    const payload = {
      name: general.name,
      description: general.description,
      tracking: [...data, ...visits],
      medications,
      diagnosticTests,
      planType: 'treatment'
    }

    // Validate required fields
    if (!payload.tracking || payload.tracking.length === 0) {
      return message.error('Please provide at least 1 health tracker for your treatment plan.')
    }

    // Make sure medications and diagnosticTests are included in the payload explicitly
    if (!payload.medications) {
      payload.medications = medications
    }

    if (!payload.diagnosticTests) {
      payload.diagnosticTests = diagnosticTests
    }

    if (isNew) {
      try {
        await createCarePlanTemplate(payload)
        message.success('Treatment plan created successfully.')
        closeTab()
        refreshView()
      } catch (error) {
        message.error(handleError(error))
      }
    } else {
      await handleUpdatePlan(payload)
    }
  }

  const handleUpdatePlan = async (payload) => {
    try {
      const updatedPayload = {
        ...recordData,
        ...payload
      }

      await updateCarePlanTemplate(updatedPayload)
      message.success('Treatment plan updated successfully.')
      closeTab()
      refreshView()
    } catch (error) {
      message.error(handleError(error))
    }
  }

  const closeTab = () => {
    dispatch(coreReducer.actions.closeTab(isNew ? 'TREATMENT_PLANS_new' : `TREATMENT_PLANS_${recordData._id}`))
  }

  // Define steps for the treatment plan wizard
  const steps = [
    {
      title: 'General',
      status: checkGeneralStatus() ? 'finish' : 'error',
      content: (
        <GeneralForm
          isMonitoring={isMonitoring}
          data={general}
          setData={setGeneral}
          planType={planType}
          planLabel={planLabel}
        />
      )
    },
    {
      title: 'Health Trackers',
      status: data.length > 0 ? 'finish' : 'wait',
      content: (
        <DataTrackerForm
          handleAddTracking={handleAddTracking}
          handleUpdateTracker={handleUpdateTracker}
          dataPoints={dataPoints}
          data={data}
          handleRemoveTracker={handleRemoveTracker}
          planType={planType}
          planLabel={planLabel}
          trackerType='health'
        />
      )
    },
    {
      title: 'Visit Trackers',
      status: visits.length > 0 ? 'finish' : 'wait',
      content: (
        <VisitTrackers
          handleAddTracking={handleAddTracking}
          visits={visits}
          handleUpdateTracker={handleUpdateTracker}
          handleRemoveTracker={handleRemoveTracker}
          planType={planType}
          planLabel={planLabel}
        />
      )
    },
    {
      title: 'Medications',
      status: medications.length > 0 ? 'finish' : 'wait',
      content: (
        <MedicationManagement
          medications={medications}
          setMedications={setMedications}
          patientRef={recordData && recordData.patientRef}
        />
      )
    },
    {
      title: 'Diagnostic Tests',
      status: diagnosticTests.length > 0 ? 'finish' : 'wait',
      content: (
        <DiagnosticsManagement
          diagnosticTests={diagnosticTests}
          setDiagnosticTests={setDiagnosticTests}
          patientRef={recordData && recordData.patientRef}
        />
      )
    },
    {
      title: 'Summary',
      status: 'wait',
      content: (
        <Summary
          general={general}
          data={data}
          visits={visits}
          isMonitoring={isMonitoring}
          setCurrentStep={setCurrentStep}
          planType={planType}
          planLabel={planLabel}
          // Include additional data for medications and diagnostics
          medicationsData={medications}
          diagnosticsData={diagnosticTests}
        />
      )
    }
  ]

  return (
    <CustomRow>
      <Col span={24}>
        <Card
          size='small'
          style={{ marginBottom: 12 }}
          title={
            <Space>
              <span>{isNew ? `Create New ${planLabel}` : `Edit ${planLabel}`}</span>
              <Button danger onClick={closeTab}>
                <FontAwesomeIcon icon={faXmarkCircle} />
              </Button>
            </Space>
          }
        >
          <Steps
            current={currentStep}
            onChange={setCurrentStep}
            size='small'
            style={{ width: '100%', overflowX: 'auto' }}
            items={steps.map((step) => ({
              title: step.title,
              status: step.status
            }))}
          />
        </Card>
      </Col>
      <Col span={24}>
        <Card
          size='small'
          extra={
            <Space>
              {currentStep > 0 && (
                <Button type='primary' onClick={() => setCurrentStep(currentStep - 1)} style={{ marginRight: 8 }}>
                  <FontAwesomeIcon icon={faChevronLeft} />
                  Previous
                </Button>
              )}

              {currentStep < steps.length - 1 ? (
                <Button
                  disabled={currentStep === 0 && !checkGeneralStatus()}
                  type='primary'
                  onClick={() => setCurrentStep(currentStep + 1)}
                >
                  Next
                  <FontAwesomeIcon icon={faChevronRight} />
                </Button>
              ) : (
                <Button type='primary' style={{ background: token.colorSuccess }} onClick={handleFinalizePlan}>
                  Finalize
                  <FontAwesomeIcon icon={faCheckCircle} />
                </Button>
              )}
            </Space>
          }
        >
          {steps[currentStep].content}
        </Card>
      </Col>
    </CustomRow>
  )
}

// Export the component with both names for backward compatibility
const PlanTemplateForm = TreatmentPlanTemplateForm
export { PlanTemplateForm as default, TreatmentPlanTemplateForm }
